// src/lib/snap_geometry.ts — memoria geometriilor EXACTE folosite la lipire (snapping).
//
// De ce există: sursa de lipire e SINCRONĂ (contract plan/contracts/snapping.md — e chemată la
// fiecare mișcare de mouse), dar geometria exactă a unui obiect vine ASINCRON, de la server. Deci
// ecranul rămâne pentru TRIAJ (ce obiecte sunt sub cursor — ieftin, prin indexarea MapLibre), iar
// geometria adevărată se ține aici și se citește instantaneu la următoarea mișcare.
//
// Ce repară: `queryRenderedFeatures` întoarce geometria DESENATĂ, nu cea din bază. MapLibre taie în
// tile-uri interne și sursele GeoJSON (`extent: 8192`, `tolerance: 0.375`), iar tile-urile noastre
// au extent 4096 — deci vârful la care se lipește funcționarul e rotunjit: în România ~10 cm la
// zoom 15 pe straturile servite ca date exacte, ~21 cm pe cele servite ca tile-uri. Sub zoom 18
// dispar și vârfuri (simplificare; la zoomul maxim al sursei toleranța e fix 0, deci niciunul).
// Două parcele arată lipite pe ecran, dar coordonatele salvate diferă — exact tiparul pe care
// raportul topologic îl găsește mai târziu ca „sliver".
//
// Modul PUR (fără DOM, fără rețea): map.client aduce datele și le pune aici.

/** Geometria unui obiect, așa cum vine din API (GeoJSON). */
export type ExactGeometry = GeoJSON.Geometry

/**
 * Cost aproximativ al unei geometrii, în număr de coordonate. Nu măsurăm octeți (ar cere
 * serializare la fiecare inserare); numărul de vârfuri e proporțional și se calculează o dată.
 */
export function geometry_size(g: ExactGeometry | null | undefined): number {
	if (!g) {
		return 0
	}
	if (g.type === "GeometryCollection") {
		return g.geometries.reduce((n, sub) => n + geometry_size(sub), 0)
	}
	const count = (v: unknown): number =>
		Array.isArray(v)
			? typeof v[0] === "number"
				? 1
				: (v as unknown[]).reduce((n: number, x) => n + count(x), 0)
			: 0
	return count((g as { coordinates?: unknown }).coordinates)
}

/**
 * Cache mărginit, cu evacuarea celei mai vechi folosiri (LRU). Plafon DUBLU — număr de intrări ȘI
 * număr total de vârfuri: pe straturile grele o singură geometrie poate avea zeci de mii de
 * coordonate, deci un plafon doar pe intrări ar lăsa memoria să crească necontrolat.
 */
export class GeometryCache {
	private readonly items = new Map<string, { geometry: ExactGeometry; size: number }>()
	private total = 0

	constructor(
		private readonly max_entries = 400,
		private readonly max_vertices = 400_000,
	) {}

	get(id: string): ExactGeometry | undefined {
		const hit = this.items.get(id)
		if (!hit) {
			return undefined
		}
		// Reinserarea mută cheia la coada ordinii de iterare a Map-ului = „folosită recent".
		this.items.delete(id)
		this.items.set(id, hit)
		return hit.geometry
	}

	has(id: string): boolean {
		return this.items.has(id)
	}

	set(id: string, geometry: ExactGeometry): void {
		const size = geometry_size(geometry)
		const old = this.items.get(id)
		if (old) {
			this.total -= old.size
			this.items.delete(id)
		}
		this.items.set(id, { geometry, size })
		this.total += size
		while (
			this.items.size > this.max_entries ||
			(this.total > this.max_vertices && this.items.size > 1)
		) {
			const oldest = this.items.keys().next()
			if (oldest.done) {
				break
			}
			const victim = this.items.get(oldest.value)!
			this.items.delete(oldest.value)
			this.total -= victim.size
		}
	}

	clear(): void {
		this.items.clear()
		this.total = 0
	}

	get size(): number {
		return this.items.size
	}

	/** Doar pentru teste/diagnostic: câte vârfuri ține cache-ul acum. */
	get vertices(): number {
		return this.total
	}
}

/**
 * Indexul id → geometrie al unei colecții deja aduse în client (sursele GeoJSON): construit LENEȘ,
 * o dată per sursă. O căutare liniară prin colecție la fiecare mișcare de mouse ar fi proastă exact
 * pe straturile mari — care ajung servite ca GeoJSON când e activă vederea la o dată din trecut.
 */
export function index_by_id(fc: GeoJSON.FeatureCollection): Map<string, ExactGeometry> {
	const index = new Map<string, ExactGeometry>()
	for (const f of fc.features) {
		const id = f.id === undefined || f.id === null ? "" : String(f.id)
		if (id && f.geometry) {
			index.set(id, f.geometry)
		}
	}
	return index
}
